import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import HeroScreen from "./marketing/HeroScreen";
import MarketingFooter from "./marketing/MarketingFooter";
import ArticleShelf from "./marketing/ArticleShelf";
import ProcessFlow from "./ProcessFlow";
import CardMotif from "./marketing/CardMotif";
import ScaleReveal from "./marketing/ScaleReveal";
import { ZP } from "../constants/palette";
import {
  AddressField,
  ARROW_NUDGE,
  BOX,
  H2,
  HAIRLINE,
  INK_BUTTON,
  KICKER,
  SHELL,
} from "./marketing/shell";

/* Flip to show the wireframe readout in the hero's right column. A flag rather
   than commenting the JSX out: commenting it leaves the HeroScreen import
   unused, and `CI=true` builds treat that warning as an error. */
const SHOW_HERO_SCREEN = false;

const STEPS = [
  {
    label: "Ask",
    title: "Type your address.",
    body: "No account, no forms, no jargon. One field is the whole first step.",
  },
  {
    label: "Read",
    title: "Get one honest answer.",
    body: "Five steps on one scale, in plain words, with every number linked to the test it came from.",
  },
  {
    label: "Act",
    title: "Fix what matters.",
    body: "The filter certification that removes it, the test worth paying for, or the budget line that ends it.",
  },
];

const AUDIENCES = [
  {
    audience: "Households",
    question: "Should I filter my water?",
    body: "See what is in the water at your address, which filter certification actually removes it, and when your system was last tested.",
  },
  {
    audience: "Real estate",
    question: "Does water affect this deal?",
    body: "Screen one property or a whole portfolio before diligence closes, then export a cited one-page record straight into the file.",
  },
  {
    audience: "Public works",
    question: "Where do we spend first?",
    body: "Rank your service areas by measured exceedance and population served, with every figure traced back to its test.",
  },
];

function Landing() {
  return (
    <div id="top" className="bg-zp-surface">
      {/* ---------------------------------------------------------------- Hero
          Full-bleed rather than boxed like the sections below it, so the video
          runs edge to edge under the floating header. Full viewport height with
          the content centred; pt clears the fixed header (24px inset + 68px
          card) so the centred block can't slide under it. */}
      <section
        id="home"
        className="relative isolate flex min-h-screen items-center overflow-hidden pt-24 sm:pt-28"
      >
        <video
          className="absolute inset-0 -z-20 h-full w-full object-cover"
          src="/hero-bg-video-1.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 -z-10 bg-black/60 backdrop-blur-[2px]"
          aria-hidden="true"
        />

        <div className={`${SHELL} w-full pb-12 sm:pb-16`}>
          {/* 65/35. fr units split what's left after the gap, so the ratio holds
              exactly rather than overflowing the way percentages would. */}
          <div className="grid items-center gap-12 lg:grid-cols-[60fr_40fr] lg:gap-14">
            <div>
              <p className={`${KICKER} text-white/65`}>
                PFAS · Lead · Arsenic · Nitrate · 40+ Contaminants
              </p>
              <h1 className="mt-6 font-display text-[2.75rem] font-thin leading-[0.99] -tracking-[0.035em] text-white sm:text-[3.5rem] lg:text-[4.25rem]">
                Millions of Americans are drinking contaminated water.
              </h1>
              <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-white/75">
                Your water quality shouldn't be guesswork. Zatoplan reads every public
                test result for the water system that serves your address, then tells you
                plainly what it means — and says nothing when nobody has measured.
              </p>
              <div className="mt-9">
                <AddressField />
              </div>
              <p className={`${KICKER} mt-5 text-white/55`}>
                Free · No account needed · Every number cited
              </p>
            </div>

            {SHOW_HERO_SCREEN && (
              <div className="hidden lg:block">
                <HeroScreen />
                <p className="sr-only">
                  Illustration: a sample Zatoplan result for Ann Arbor, Michigan, scored
                  High, with nitrate shown as never measured.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- Boxed light sections */}
      <div className={`${SHELL} space-y-5 py-5`}>
        {/* 01 — Using it: the reader's own journey (the pipeline is How it
            works, on Features) */}
        <section className={`${BOX} bg-transparent`}>
          <p className={`${KICKER} text-zp-slate`}>01 / Using it</p>
          <h2 className={`${H2} mt-4 text-zp-ink`}>Ask. Read. Act.</h2>

          <div className="mt-9 grid gap-3 md:grid-cols-3">
            {STEPS.map(({ label, title, body }, index) => {
              const surface = [
                { panel: "bg-zp-ink", text: "text-white", motif: "bg-white/20" },
                {
                  panel: "bg-zp-ink/10 border",
                  text: "text-zp-ink",
                  motif: "bg-zp-ink/[0.13]",
                },
                {
                  panel: "bg-white border",
                  text: "text-zp-ink",
                  motif: "bg-zp-ink/[0.10]",
                },
              ][index];

              return (
                <div
                  key={label}
                  className={`relative isolate flex min-h-[24rem] flex-col justify-between overflow-hidden rounded-lg p-7 sm:p-8 ${surface.panel}`}
                >
                  {/* Bauhaus primitives — circle, triangle, half-circle —
                      bleeding off the panel corner. */}
                  {/* <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute -z-10 ${surface.motif} ${
                      [
                        "-bottom-20 -right-14 h-64 w-64 rounded-full",
                        "-bottom-16 -right-10 h-60 w-64",
                        "-bottom-28 -right-12 h-64 w-64 rounded-t-full",
                      ][index]
                    }`}
                    style={
                      index === 1
                        ? { clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }
                        : undefined
                    }
                  /> */}

                  <div className="flex items-center justify-between">
                    <span className={`${KICKER} ${surface.text}`}>{label}</span>
                    <span className={`font-mono text-[11px] opacity-60 ${surface.text}`}>
                      0{index + 1}
                    </span>
                  </div>

                  {/* Each step's drawing, in the card's ink: white on the
                      ink card, ink on the two light ones. */}
                  <div className="flex justify-center py-8">
                    <CardMotif
                      kind={label.toLowerCase()}
                      ink={index === 0 ? ZP.bg : ZP.ink}
                    />
                  </div>

                  <div>
                    <h3
                      className={`font-display text-[1.85rem] font-semibold leading-tight -tracking-[0.025em] ${surface.text}`}
                    >
                      {title}
                    </h3>
                    <p
                      className={`mt-3 text-[15px] leading-relaxed opacity-85 ${surface.text}`}
                    >
                      {body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        {/* 02 — The scale */}
        <section className={`${BOX} bg-zp-bg`}>
          <p className={`${KICKER} text-zp-slate`}>02 / The scale</p>
          <h2 className={`${H2} mt-4 text-zp-ink`}>Five steps, in plain words.</h2>
          <p className="mt-5 max-w-[44rem] leading-relaxed text-zp-body">
            Every water system lands on the same five-step scale, built from the same
            measured limits. When a contaminant has never been tested for, it gets no step
            at all — it gets a blank.
          </p>

          {/* A few example places that reveal their answers — the whole scale,
              and the blank, learned by playing with it. Replaces six saturated
              tiles, which were the most colour on the site and none of it
              earned by a reading. ScaleZoom (the country-to-address zoom) is
              the alternative, kept for another section. */}
          <div className="mt-10">
            <ScaleReveal />
          </div>
        </section>
      </div>

      {/* 03 — Chaos to clarity. ProcessFlow is the animated argument for the
          pipeline: labelled source particles drift on the left, resolve through
          the six stages, and come out as one ordered record.

          This section sits OUTSIDE the boxed shell on purpose, so the animation
          can run the full width of the viewport instead of being held to the
          1440/40px container. Only the heading keeps the shell's gutters, so it
          still lines up with the sections above and below. The page background
          is already zp-surface, so dropping the panel changes no colour. */}
      <section id="whyzato" className="py-12 sm:py-20">
        <div className={`${SHELL} text-center`}>
          <p className={`${KICKER} text-zp-slate`}>03 / Chaos to clarity</p>
          <h2 className={`${H2} my-4 text-zp-ink`}>Turning chaos into clarity.</h2>
          <p className="max-w-xl mx-auto leading-relaxed text-zp-body">
            In the United States, water data flows through a maze of agencies, each
            managing its own piece of the puzzle — fragmented, with little coordination or
            consistency. Data sits in silos, unstructured and difficult to access, leaving
            decision-makers with an incomplete picture. We reconcile it into one record.
          </p>
          {/* The animation below is the summary; How it works, on the Features
              page, is the full account. Styled as the nav's primary CTA, on
              this light surface. */}
          <Link
            to="/features#how-it-works"
            className={`${KICKER} mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-zp-ink px-5 py-3.5 text-[10.5px] text-white ${INK_BUTTON}`}
          >
            See how it works
            <ArrowRight
              className={`h-[13px] w-[13px] ${ARROW_NUDGE}`}
              aria-hidden="true"
            />
          </Link>
        </div>

        {/* Gutters below md only: that breakpoint is the card fallback inside
            ProcessFlow, which shouldn't touch the viewport edges. The animation
            itself is md+ and goes truly edge to edge. */}
        <div className="mt-10 w-full px-5 sm:mt-14 md:px-0">
          <ProcessFlow />
        </div>
      </section>

      <div className={`${SHELL} space-y-5 py-5`}>
        {/* 04 — The thesis */}
        <section className={`${BOX} bg-zp-night sm:py-20`}>
          <p className={`${KICKER} text-white/50`}>04 / The thesis</p>
          <h2 className={`${H2} mt-4 max-w-[60rem] text-white`}>
            We would rather show you a blank than a guess.
          </h2>
          <p className="mt-6 max-w-[44rem] text-[17px] leading-relaxed text-white/70">
            Estimates are useful, and we publish them — labelled, separately, never folded
            into a measured score. When a contaminant has never been tested for your
            system, the page says exactly that. The gaps are part of the finding.
          </p>
          {/* The sources list moved to Features; this keeps the way to it. */}
          <Link
            to="/features#sources"
            className={`${KICKER} mt-8 inline-flex items-center gap-2 text-[10.5px] text-white/80 transition-colors hover:text-white`}
          >
            Where the numbers come from
            <ArrowRight className={`h-[13px] w-[13px] ${ARROW_NUDGE}`} aria-hidden="true" />
          </Link>
        </section>
        {/* 05 — Who uses it */}
        <section className={`${BOX} bg-zp-surface`}>
          <p className={`${KICKER} text-zp-slate`}>05 / Who uses it</p>
          <h2 className={`${H2} mt-4 text-zp-ink`}>Three questions. One record.</h2>

          <div className="mt-9 border-t border-zp-ink">
            {AUDIENCES.map(({ audience, question, body }, index) => (
              <div
                key={audience}
                className={`flex flex-col gap-5 py-8 lg:flex-row lg:items-center lg:gap-11 ${
                  index > 0 ? `border-t ${HAIRLINE}` : ""
                }`}
              >
                <span
                  className="font-mono text-3xl font-light text-zp-ink/25 lg:w-20 lg:shrink-0"
                  aria-hidden="true"
                >
                  0{index + 1}
                </span>
                <div className="lg:w-[20.5rem] lg:shrink-0">
                  <p className={`${KICKER} text-zp-slate`}>{audience}</p>
                  <h3 className="mt-2.5 font-display text-2xl font-semibold leading-tight -tracking-[0.02em] text-zp-ink">
                    {question}
                  </h3>
                </div>
                <p className="flex-1 text-[15.5px] leading-relaxed text-zp-body">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <ArticleShelf />

        {/* 06 — CTA */}
        <section className={`${BOX} bg-zp-night sm:py-20`}>
          <p className={`${KICKER} text-white/50`}>06 / Start here</p>
          <h2 className="mt-4 font-display text-[2.5rem] font-light leading-none -tracking-[0.04em] text-white sm:text-5xl lg:text-[4rem]">
            Check your address.
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-white/70">
            One field. No account. Plain answers, with every source shown.
          </p>
          <div className="mt-9">
            <AddressField action="Check my water" />
          </div>
        </section>
        <MarketingFooter />
      </div>
    </div>
  );
}

export default Landing;
